<template>
  <div>
    <VCard class="int-card">
      <div class="int-vacancy-head">
        <!--        {{ $t('vacancy-head-text') }}-->
        <div class="int-vacancy-check">
          <VCheckbox :indeterminate="checkAllIndeterminate" v-model="checkAll" hide-details />
        </div>
        <div class="int-vacancy-search">
          <VIcon>mdi-magnify</VIcon>
          <input
            type="text"
            placeholder="Поиск по названию"
            class="int-vacancy-search__field"
            v-model="searchText"
          >
        </div>
        <div class="int-vacancy-search int-vacancy-search_city">
          <VIcon>mdi-magnify</VIcon>
          <input
            type="text"
            placeholder="Поиск по городу"
            class="int-vacancy-search__field"
            v-model="searchTextCity"
          >
        </div>
      </div>
      <VCardText v-if="errorMessage">
        <ErrorsDefault
          :error-message="errorMessage"
          repeat-btn
          @repeat="save"
          :handle-route="{
            name: 'dashboard'
          }"
        />
      </VCardText>
      <VCardText v-else-if="loading">
        <Preloader />
      </VCardText>
      <template v-else-if="externalVacancies.length">
        <div class="int-vacancy-wrap">
          <template v-if="filteredVacancies.length">
            <div
              class="int-vacancy-item-wrap px-4 px-md-10"
              v-for="(item) in filteredVacancies"
              :key="item.id"
            >
              <IntegrationsVacancyListItem
                :selected="isSelected(item)"
                :vacancy="item"
                @add="add"
                @remove="remove"
              />
            </div>
          </template>
          <VCardText v-else>
            <IntegrationsVacancyListEmpty>
              По заданным условиям вакансии не найдены
            </IntegrationsVacancyListEmpty>
          </VCardText>
        </div>
        <div class="int-vacancy-btn-wrap py-2">
          <VBtn
            @click="save"
            width="9.125rem"
            color="#1FC57F"
            depressed
            class="int-btn d-flex mx-auto"
          >
            {{ $t('next') }}
          </VBtn>
          <VBtn
            v-if="cancel"
            @click="onCancel"
            width="9.125rem"
            color="#1FC57F"
            depressed
            class="int-btn d-flex mx-auto"
          >
            {{ $t('cancel') }}
          </VBtn>
        </div>
      </template>
      <VCardText v-else>
        <IntegrationsVacancyListEmpty />
        <VBtn
          @click="$emit('saved')"
          width="9.125rem"
          color="#1FC57F"
          depressed
          class="int-btn d-flex mx-auto"
        >
          {{ $t('next') }}
        </VBtn>
      </VCardText>
    </VCard>
  </div>
</template>

<script>
import IntegrationVacancy from '@/orm/models/Integration/IntegrationVacancy';
import IntegrationsVacancyListItem from '@/components/Integrations/IntegrationsVacancyListItem';
import Vacancy from '@/orm/models/Vacancy/Vacancy';
import useValidation from '@/compositions/useValidation';
import { computed, ref, watch } from '@vue/composition-api';
import useUser from '@/compositions/useUser';
import useIntegrationVacancy from '@/compositions/useIntegrationVacancy';
import IntegrationsVacancyListEmpty from '@/components/Integrations/IntegrationsVacancyListEmpty';
import Preloader from '@/components/Preloader';
import User from '@/orm/models/User/User';
import ErrorsDefault from '@/components/Errors/ErrorsDefault';
import escapeRegExp from 'lodash.escaperegexp';

export default {
  name: 'IntegrationsVacancyList',
  components: {
    ErrorsDefault, Preloader, IntegrationsVacancyListEmpty, IntegrationsVacancyListItem,
  },
  props: {
    vacancies: {
      type: Array,
      default: () => [],
    },
    cancel: Boolean,
  },
  setup({ vacancies }, { emit }) {
    const {
      withApiErrorHandling, loading, errorMessage,
      hasErrors,
    } = useValidation();
    const integration = ref({
      id: 1,
      code: 'rabotaru',
    });
    const { notLinkedExternalVacancies } = useIntegrationVacancy({
      integration: integration.value,
      vacancies,
      callback: () => {
        if (!notLinkedExternalVacancies.value?.length) {
          emit('next');
        }
      },
    });

    const { company } = useUser();
    const selected = ref([]);
    const searchText = ref('');
    const searchTextCity = ref('');

    const isAllChecked = computed(() => {
      return selected.value.length > 0
        && selected.value.length === notLinkedExternalVacancies.value.length;
    });

    const checkAll = ref(false);
    const checkAllIndeterminate = computed(() => {
      return selected.value.length > 0
        && selected.value.length !== notLinkedExternalVacancies.value.length;
    });

    watch(checkAll, () => {
      if (!checkAll.value) {
        selected.value = [];
      }
    });

    const filteredVacancies = computed(() => {
      let result = notLinkedExternalVacancies.value;

      if (searchText.value) {
        result = result.filter((i) => {
          return i.title.match((new RegExp(escapeRegExp(searchText.value), 'gi')));
        });
      }

      if (searchTextCity.value) {
        result = result.filter((i) => {
          return i.cities[0].match((new RegExp(escapeRegExp(searchTextCity.value), 'gi')));
        });
      }

      return result;
    });

    watch(filteredVacancies, () => {
      selected.value = selected.value.filter((id) => {
        return filteredVacancies.value.some((v) => v.id === id);
      });
    });

    function isSelected(i) {
      return (checkAll.value && !searchText.value) || selected.value.includes(i.id);
    }

    watch(searchTextCity, () => {
      selected.value = selected.value.filter((id) => id === filteredVacancies.value.some((v) => v.id === id));
    });

    function add(vacancyId) {
      selected.value.push(vacancyId);
      if (isAllChecked.value) {
        checkAll.value = true;
      }
    }

    function remove(vacancyId) {
      selected.value = selected.value.filter((v) => v !== vacancyId);
      if (!selected.value.length) {
        checkAll.value = false;
      }
    }

    async function save() {
      if (selected.value.length) {
        await withApiErrorHandling(async () => {
          await IntegrationVacancy.api().import(
            User.getAuthUser().company.id,
            integration.value.id,
            { ids: this.selected },
          );
          await Vacancy.api().fetchAllByCompanyId(company.value?.id);
        });
      }
      if (!hasErrors.value) {
        emit('saved');
      }
    }

    function onCancel() {
      emit('cancel');
    }

    return {
      integration,
      selected,
      externalVacancies: notLinkedExternalVacancies,
      save,
      onCancel,
      remove,
      add,
      errorMessage,
      loading: computed(() => loading.value),
      searchText,
      searchTextCity,
      filteredVacancies,
      checkAll,
      checkAllIndeterminate,
      isSelected,
    };
  },
};
</script>

<style lang="scss" scoped>
  .int-card.v-card {
    width: 100%;
    max-width: 62.5rem;
    border-radius: 0.625rem;
    margin: 0 auto;
    &:not(.v-sheet--outlined) {
      box-shadow: 0px 0.5rem 1rem rgba(0, 0, 0, 0.04);
    }
  }
  .int-vacancy-head {
    display: flex;
    color: #091A35;
    padding: 0.375rem 2.5rem 0.5rem 2.5rem;
    position: relative;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    /*&:after {
      content: '';
      display: block;
      width: 100%;
      height: 0.625rem;
      opacity: 0.4;
      position: absolute;
      left: 0;
      background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000000 330%);
      bottom: -0.625rem;
      transform: matrix(1, 0, 0, -1, 0, 0);
    }*/
  }
  .int-vacancy-search {
    display: flex;
    flex-grow: 2;
    padding-top: 0.25rem;
    &_city {
      max-width: 20.875rem;
      padding-left: 1rem;
      justify-self: flex-end;
    }
    i {
      margin-right: 0.375rem;
    }
    &__field {
      font-size: 0.9375rem;
      color: #091A35;
      &:focus {
        outline: none;
      }
    }
  }
  .int-vacancy-wrap {
    position: relative;
    max-height: 37rem;
    min-height: calc(90vh - 17.5rem);
    overflow: auto;
  }

  .int-vacancy-btn-wrap {
    position: relative;
    &:before {
      content: '';
      display: block;
      width: 100%;
      height: 0.625rem;
      opacity: 0.4;
      position: absolute;
      left: 0;
      background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000000 330%);
      top: -0.625rem;
    }
  }
  .int-vacancy-item-wrap:last-child > div {
    border-bottom: none;
  }

  @media #{map-get($display-breakpoints, 'sm-and-down')} {
    .int-vacancy-head {
      padding: 0.375rem 1rem 0.5rem 1rem;
    }
    .int-vacancy-wrap {
      max-height: none;
    }
    .int-not-vacancy {
      img {
        max-width: 7rem;
      }
    }
    .int-vacancy-search {
      &_city {
        max-width: 16.375rem;
      }
    }
  }

  @media #{map-get($display-breakpoints, 'xs-only')} {
    .int-vacancy-head {
      flex-wrap: wrap;
    }
    .int-vacancy-search_city {
      width: 100%;
      padding-left: 2rem;
    }
  }

  @media screen and (max-width: 374px) {
    .int-vacancy-head {
      padding: 0.375rem 0.9375rem 0.5rem 0.9375rem;
    }
  }

</style>

<i18n lang="yml" locale="en">
  next: Next
  cancel: Cancel
  vacancy-head-text: Jobs on the website Rabota.ru
  vacancy-not: You have no open jobs on rabota.ru
</i18n>
<i18n lang="yml" locale="ru">
  next: Далее
  cancel: Отмена
  vacancy-head-text: Вакансии на сайте Rabota.ru
  vacancy-not: У вас нет открытых вакансий на сайте rabota.ru
</i18n>
